import React, { useEffect, useMemo, useRef } from 'react';
import { Autocomplete, Box, Popper, TextField, Typography } from '@mui/material';
import { useDialingCodes } from 'contexts/DialingCodesContext';

const WidePopper = props => (
    <Popper
        {...props}
        style={{ width: 340, maxWidth: 'calc(100vw - 32px)' }}
        placement="bottom-start"
        modifiers={[
            {
                name: 'flip',
                enabled: false
            }
        ]}
    />
);

export default function CountryCodeSelect({
    value,
    onChange,
    defaultRegion = 'US',
    label = 'Country Code',
    size = 'medium',
    sx,
    disabled = false
}) {
    const { countries = [] } = useDialingCodes();
    const appliedDefaultRegionRef = useRef(null);

    const sortedCountries = useMemo(() => {
        return [...countries].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, {
                sensitivity: 'base'
            })
        );
    }, [countries]);

    const defaultCountry = useMemo(() => {
        return (
            countries.find(country => country.region === defaultRegion) ||
            countries[0] ||
            null
        );
    }, [countries, defaultRegion]);

    const selectedValue = value || defaultCountry;

    useEffect(() => {
        if (value || !defaultCountry) return;

        if (appliedDefaultRegionRef.current === defaultCountry.region) return;

        appliedDefaultRegionRef.current = defaultCountry.region;
        onChange?.(defaultCountry);
    }, [value, defaultCountry]);

    return (
        <Autocomplete
            options={sortedCountries}
            disabled={disabled}
            value={selectedValue}
            onChange={(_, newValue) => onChange?.(newValue)}
            autoHighlight
            disableClearable
            PopperComponent={WidePopper}
            filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();

                if (!query) return options;

                const digitsOnly = query.replace(/\D/g, '');

                return options.filter(option => {
                    const nameMatch = (option.name || '').toLowerCase().includes(query);

                    const codeMatch = digitsOnly
                        ? String(option.code || '')
                              .replace(/\D/g, '')
                              .includes(digitsOnly)
                        : false;

                    return nameMatch || codeMatch;
                });
            }}
            getOptionLabel={option => option?.code || ''}
            isOptionEqualToValue={(option, selected) =>
                option.region === selected?.region
            }
            renderOption={(props, option) => {
                const { key, ...rest } = props;

                return (
                    <Box
                        component="li"
                        {...rest}
                        key={option.region}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 1
                        }}
                    >
                        <Box sx={{ width: 26, textAlign: 'center', fontSize: 18 }}>
                            {option.flag}
                        </Box>

                        <Typography sx={{ fontWeight: 600, minWidth: 64 }}>
                            {option.code}
                        </Typography>

                        <Typography sx={{ color: 'text.secondary' }}>
                            {option.name}
                        </Typography>
                    </Box>
                );
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    disabled={disabled}
                    inputProps={{
                        ...params.inputProps,
                        style: { paddingLeft: 0 }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                <Box
                                    sx={{
                                        width: 22,
                                        textAlign: 'center',
                                        fontSize: 18
                                    }}
                                >
                                    {selectedValue?.flag ?? ''}
                                </Box>
                            </Box>
                        )
                    }}
                />
            )}
            sx={{
                minWidth: { xs: 90, sm: 120 },
                '& .MuiAutocomplete-inputRoot': {
                    pr: 1
                },
                ...sx
            }}
        />
    );
}